<!--
  The editor of an output context, as a button-group in a orderable-item,
  with a slidedown

  Props:
  - value: the value
      ex: {
            context: "context_name" || "followup:intentid",
            ttl: 3, // -1 for unlimited, 0 for delete
            exclusive: false,
            follow: false, // follow all local variable to the context
            extend: false, // refresh the context instead of replacing it
            variables: { // variables to pass to the context
              'var1': '123',
              'var2': '@value'
            },
            script: "...",
          }
          "context" // for newItem
  - other: a value to compare with
  - prop: the prop prefix to use for el-form-item
  - required: required
  - rules: extra rules for the context
  - typeRules: extra rules for the type
  - ttlRules: extra rules for the ttl
  - active: if it should open its dropdown
  - newInput: true for a new input, with only the context input enabled
  - header: true for displaying only the "table" headers
  - placeholder: the text to display for an empty context
  - intentid: the current intent
  - contexts: all the known contexts
  - intents: all the available intents
  - localVariables: for el-code-input
  - contextVariables: for el-code-input
  - variableSuggestions: for el-code-input

  Emits:
  - @input: the new value
  - @change: same as input
  - @activate
  - @delete
-->

<template>
  <comparator-filter>
    <template v-slot="{ compare }">
      <orderable-item
        :disabled="header || compare"
        :new-item="newInput"
        v-bind="$attrs"
        v-on="$listeners"
        @click.native="handleActivate">
        <!-- "table" headers -->
        <input-headers-group v-if="header">
          <input-header width="78px">More</input-header>
          <input-header>Context</input-header>
          <input-header width="195px">
            Type of context
            <template slot="tooltip">
              Changes the duration and the behavior of the <em>context</em>
            </template>
          </input-header>
          <input-header width="56px"></input-header>
        </input-headers-group>
        <!-- main editor -->
        <template v-else>
          <el-form-errors>
            <el-button-group style="width: 100%">
              <tooltip-button v-if="newInput"
                placement="bottom-start">
                Output contexts update the state of the chatbot, <br/>
                by opening new dialog possibilities and saving variables
              </tooltip-button>
              <!-- button to open the slidedown -->
              <el-button v-else
                style="padding: 12px" type="info"
                :icon="output.type !== 'delete' && variablesButton.icon || 'el-icon-minus'"
                :disabled="output.type === 'delete' || variablesButton.disabled"
                @click.stop="handleActivate(variablesButton.next)"
                :tabindex="-1">
              </el-button>
              <!-- button for reordering -->
              <orderable-item-button></orderable-item-button>
              <!-- checbox for context or followup -->
              <el-checkbox border
                @click.native.stop
                v-model="followup"
                :disabled="compare">
                Followup
                <tooltip-icon placement="bottom">
                  Select to choose a <em>followup context</em>
                  instead of a normal <em>context</em>.
                </tooltip-icon>
              </el-checkbox>
              <comparable prop="value.context"
                :required="!newInput"
                :rules="computedRules"
                @rules="val => contextRules = val"
                @update:followup="val => followup = val">
                <template v-slot="{ view, compare, attrs, listeners, status }">
                  <el-form-small-item :class="'comparable-' + status"
                    v-bind="attrs"
                    :prop="view && prop && prop+'.context'"
                    field="context">
                    <!-- context input -->
                    <el-context-input v-if="!followup"
                      ref="input"
                      v-model="context"
                      :disabled="compare"
                      :placeholder="placeholder"
                      :contexts="contexts">
                    </el-context-input>
                    <!-- followup input -->
                    <el-filterable-select v-else
                      ref="input"
                      v-model="intent"
                      :disabled="compare"
                      :placeholder="intentid ? 'This intent' : 'Choose an intent'"
                      :clearable="!!intentid"
                      :options="intentOptions"
                      label-field="name">
                      <template slot="option" slot-scope="{item}">
                        <span v-if="item.context"
                          class="followup-context-editor__prefix"
                          >[{{item.context}}] </span>
                        <span class="el-filterable-select__left"
                          >{{ item.left }}</span>
                        <strong class="el-filterable-select__match"
                          >{{ item.match }}</strong>
                        <span class="el-filterable-select__right"
                          >{{ item.right }}</span>
                      </template>
                    </el-filterable-select>
                  </el-form-small-item>
                </template>
              </comparable>
              <!-- link to the context or followup intent -->
              <router-link v-if="!followup"
                style="padding: 12px"
                :to="{
                  name: 'skill',
                  params: {skill: $route.params.skill},
                  query: $route.query,
                  hash: context ? '#context-' + context : '#global-context',
                }"
                target="_blank"
                class="el-button"
                :tabindex="-1">
                <i class="el-icon-link"></i>
              </router-link>
              <router-link v-else-if="intent"
                style="padding: 12px"
                :to="{
                  name: 'intent',
                  params: {skill: $route.params.skill, intent: intent},
                  query: $route.query
                }"
                target="_blank"
                class="el-button"
                :tabindex="-1">
                <i class="el-icon-link"></i>
              </router-link>
              <el-button v-else disabled
                style="padding: 12px"
                :tabindex="-1">
                <i class="el-icon-link"></i>
              </el-button>
              <!-- output type selector -->
              <comparable field="type"
                :prop="({ value: v }) => v.exclusive ? 'x' : Math.sign(v.ttl)"
                :rules="typeRules">
                <template v-slot="{ view, compare, attrs, status }">
                  <el-form-small-item style="flex: 0 1 120px"
                    :class="'comparable-' + status"
                    v-bind="attrs"
                    :prop="view && prop">
                    <el-select
                      v-model="output.type"
                      :disabled="newInput || compare">
                      <el-option
                        v-for="type in contextTypes"
                        :key="type.value"
                        :value="type.value">
                        {{ type.name }}
                        <tooltip-icon v-if="type.tooltip" placement="right">
                          <span v-html="type.tooltip"></span>
                        </tooltip-icon>
                      </el-option>
                    </el-select>
                  </el-form-small-item>
                </template>
              </comparable>
              <!-- ttl input -->
              <comparable field="ttl"
                :prop="({ value: v }) => v.exclusive || v.ttl <= 0 ? 0 : v.ttl"
                :rules="ttlRules">
                <template v-slot="{ view, compare, attrs, status }">
                  <el-form-small-item style="flex: 0 1 75px"
                    :class="'comparable-' + status"
                    :prop="view && prop"
                    v-bind="attrs">
                    <el-input-number
                      v-model="output.ttl"
                      controls-position="right"
                      :disabled="newInput || compare || output.type !== 'temporary'"
                      :min="1"></el-input-number>
                  </el-form-small-item>
                </template>
              </comparable>
              <!-- delete button -->
              <el-button
                :icon="newInput ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                @click="handleDelete"
                :tabindex="-1"
                :disabled="newInput || compare">
              </el-button>
            </el-button-group>
          </el-form-errors>
          <!-- variables editor -->
          <variables-editor
            v-if="!newInput && output.type !== 'delete'"
            v-model="output.variables"
            :other="other && (other.variables || {})"
            :compare="compare"
            :prop="prop && prop+'.variables'"
            :active="active"
            :forced="output.extend || output.follow || other && (other.extend || other.follow)"
            @button="val => variablesButton = val"
            :local-variables="localVariables"
            :context-variables="contextVariables"
            :variable-suggestions="variableSuggestions"
            :varstatus="varstatus"
            label="Context variables">
            <!-- checkbox for entend (refresh) the context -->
            <comparable prop="value.extend" :default="false" diff>
              <template v-slot="{ status }">
                <el-checkbox :class="'comparable-' + status"
                  v-model="output.extend"
                  >Keep previous variables
                  <tooltip-icon placement="top">
                    Refresh the <em>context</em> variables instead or replacing them.
                  </tooltip-icon>
                </el-checkbox>
              </template>
            </comparable>
            <!-- checkbox to follow the local variables -->
            <comparable prop="value.follow" :default="false" diff>
              <template v-slot="{ status }">
                <el-checkbox :class="'comparable-' + status"
                  v-model="output.follow"
                  >Follow all local variables
                  <tooltip-icon placement="top">
                    Follows all the <code>@var</code> local variables to the <em>context</em> as <code>$var</code>.
                  </tooltip-icon>
                </el-checkbox>
              </template>
            </comparable>
            <!-- optional script input -->
            <el-collapsable slot="after"
              v-model="advanced"
              :forced="!!output.script || other && other.script">
              <el-tooltip placement="top" effect="light" :tabindex="-1">
                <el-alert
                  type="warning"
                  show-icon
                  :closable="false">
                  <template slot="title">
                    <code>$var</code> variables represent the <strong><em>output context</em></strong>. Use <code>$$context_name.var</code> variables to access to the <em>input context</em>.
                  </template>
                </el-alert>
                <template slot="content">
                  Edit the <em>context variables </em><code>$variable</code> of the newly created <em>output context</em>.<br/>
                  Beware that <code>$variable</code> variables represent the <em>output context</em>.<br/>
                  Use <code>$$context_name.variable</code> variables to access to the <em>input context</em>.
                </template>
              </el-tooltip>
              <comparator :view="value" :compare="other"
                prop="script" default="">
                <template v-slot="{ value, view, compare, status }">
                  <el-form-item-code-input :class="'comparable-' + status"
                    :prop="view && prop && prop+'.script'"
                    type="program"
                    v-model="value.script"
                    :varstatus="scriptstatus"
                    :local-variables="localVariables"
                    :context-variables="variableSuggestions"
                    :autosize="{minRows: 5, maxRows: 20}"
                    :disabled="compare">
                  </el-form-item-code-input>
                </template>
              </comparator>
            </el-collapsable>
          </variables-editor>
        </template>
      </orderable-item>
    </template>
  </comparator-filter>
</template>

<script>
import $ from 'jquery'
import { VariableValidator } from '../../utils/validators'

import OrderableItem from '../orderable/OrderableItem.vue'
import OrderableItemButton from '../orderable/OrderableItemButton.vue'
import ElContextInput from '../element/ElContextInput.vue'
import ElFilterableSelect from '../element/ElFilterableSelect.vue'
import VariablesEditor from './VariablesEditor.vue'
import ElFormItemCodeInput from '../element/ElFormItemCodeInput.vue'
import ElCollapsable from '../element/ElCollapsable.vue'

const DEFAULT_TTL = 3

const VAR_STATUS = {
  local: 'readonly',
  context: 'readonly',
  contexts: 'readonly',
}
const SCRIPT_STATUS = {
  local: 'readonly',
  context: 'editable',
  contexts: 'readonly',
}

// context types
const contextTypes = [{
  // expires after TTL turns
  value: 'temporary',
  name: 'Temporary',
  tooltip: 'the <em>context</em> will exist for <code>TTL</code> interactions',
}, { // never expires
  value: 'unlimited',
  name: 'Unlimited',
  tooltip: 'the <em>context</em> will exist until deleted',
}, { // disable other contexts, expires after 1 turn
  value: 'exclusive',
  name: 'Exclusive',
  tooltip: 'only the <em>intents</em> of this <em>context</em> will be matched during the next interaction',
}, { // expires an existing context
  value: 'delete',
  name: 'Delete',
  tooltip: 'the <em>context</em> is deleted',
}]

export default {
  components: {
    OrderableItem,
    OrderableItemButton,
    ElContextInput,
    ElFilterableSelect,
    VariablesEditor,
    ElFormItemCodeInput,
    ElCollapsable,
  },
  props: {
    placeholder: String, // the text to display for an empty context
    contexts: Array, // all the known contexts
    intents: Array, // all the available intents
    intentid: String, // the current intent
    value: [String, Object],
    other: Object, // a value to compare with
    active: { // if it should open its dropdown
      type: Boolean,
      default: true,
    },
    // true for a new input, with only the context input enabled
    newInput: Boolean,
    header: Boolean, // true for displaying only the "table" headers
    localVariables: Array, // for el-code-input
    contextVariables: Array, // for el-code-input
    variableSuggestions: Array, // for el-code-input

    prop: String, // the prop prefix to use for el-form-item
    rules: [Object, Array], // extra rules for the context
    typeRules: [Object, Array], // extra rules for the type
    ttlRules: [Object, Array], // extra rules for the ttl
  },
  data () {
    // followup context validator
    const FollowupContextValidator = {
      validator: (rule, value, callback) => {
        if (!value || value.substr(0, 'followup:'.length) !== 'followup:') {
          return callback(new Error('invalid value'))
        }
        value = value.substr('followup:'.length)
        if (!value) return callback(new Error('intent required'))
        return callback()
      },
      trigger: 'blur',
    }
    return {
      output: { // the model
        context: '',
        variables: {},
        script: '',
        type: 'temporary',
        extend: false,
        follow: false,
        ttl: DEFAULT_TTL,
      },
      variablesButton: {}, // button status from VariablesEditor
      contextTypes: contextTypes, // availbale context types
      varstatus: VAR_STATUS, // for el-code-input
      scriptstatus: SCRIPT_STATUS, // for el-code-input
      DEFAULT_TTL: DEFAULT_TTL, // the TTL to use on creation
      advanced: false, // the advanced slidedown for the script

      followup: false, // the followup checkbox
      context: '', // the context content
      intent: '', // the followup selector intentid
      contextRules: [{
        // use VariableValidator for a free context
        ...VariableValidator,
        validator: (rule, value, callback) => {
          if (this.followup) return callback()
          return VariableValidator.validator(rule, value, callback)
        },
      }, {
        // use FollowupContextValidator for a followup context
        ...FollowupContextValidator,
        validator: (rule, value, callback) => {
          if (!this.followup) return callback()
          return FollowupContextValidator.validator(rule, value, callback)
        },
      }],
    }
  },
  // init output
  created () {
    this.updateOutput(this.value)
  },
  watch: {
    // update output
    value (val) { this.updateOutput(val) },
    // emit the new value
    output: {
      handler (output) {
        this.outputUpdated(output)
      },
      deep: true,
    },
    // update the context (emit it for new item)
    computedContext (context) {
      this.output.context = context
    },
  },
  computed: {
    // merge rules
    computedRules () {
      return [].concat(this.contextRules, this.rules || [])
    },
    // the context value
    computedContext () {
      if (!this.followup) return this.context
      else if (this.intent) return 'followup:' + this.intent
      else if (this.intentid) return 'followup:' + this.intentid
      else return 'followup:'
    },
    // the list of available intents, with eventually this intent
    intentOptions () {
      if (!this.intentid) return this.intents
      let out = this.intents.filter(x => x.value !== this.intentid)
      out.unshift({
        name: '[This intent]',
        value: '',
      })
      return out
    },
  },
  methods: {
    // focus the context input
    focus () {
      $(this.$refs.input.$el).find('input').focus()
      this.handleActivate()
    },
    // leave the context input
    blur () {
      $(this.$refs.input.$el).find('input').blur()
    },
    // update output from value
    updateOutput (value) {
      // new input, only update the context
      value = value || {}
      // copy the value
      let output = {
        context: value.context || '',
        script: value.script || '',
        variables: value.variables || {},
        extend: !!value.extend,
        follow: !!value.follow,
      }
      // update the followup checkbox
      this.followup = !!value.context &&
        value.context.substr(0, 'followup:'.length) === 'followup:'
      // update context and intent fields
      if (this.followup) {
        let intent = value.context.substr('followup:'.length)
        if (intent === this.intentid) intent = ''
        this.intent = intent
      } else this.context = value.context
      // deduce the type of context
      if (value.exclusive) output.type = 'exclusive'
      else if (value.ttl < 0) output.type = 'unlimited'
      else if (value.ttl === 0) output.type = 'delete'
      else {
        output.type = 'temporary'
        output.ttl = value.ttl || (this.followup ? 1 : DEFAULT_TTL)
      }
      // check if variables is empty for both
      let emptyvar = true
      for (let _ in output.variables) emptyvar = false
      for (let _ in this.output.variables) emptyvar = false
      // compare the current output to the new one
      if (output.extend !== !!this.output.extend ||
          output.follow !== !!this.output.follow ||
          output.context !== this.output.context ||
          output.script !== this.output.script ||
          output.type !== this.output.type ||
          output.type === 'temporary' && output.ttl !== this.output.ttl ||
          !emptyvar && output.variables !== this.output.variables) {
        // save the new output if different
        this.output = output
        return
      }
    },
    // output has changed, emit the new value
    outputUpdated (output) {
      // build the new value
      let value = {
        context: output.context,
        extend: output.extend,
        follow: output.follow,
        variables: output.variables,
      }
      if (output.script) value.script = output.script
      // set ttl and exclusive depending on the type
      if (output.type === 'exclusive') {
        value.exclusive = true
        value.ttl = 1
      } else if (output.type === 'unlimited') {
        value.ttl = -1
      } else if (output.type === 'delete') {
        value.ttl = 0
      } else if (output.type === 'temporary') {
        value.ttl = output.ttl
      }
      // emit
      this.$emit('input', value)
      this.$emit('change', value)
    },
    // emit activate
    handleActivate (activate) {
      if (this.newInput || this.header) return
      this.$emit('activate', activate !== false)
    },
    // emit delete
    handleDelete () {
      this.$emit('delete')
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .followup-context-editor__prefix {
    color: $--color-text-placeholder;
  }
</style>
